import { TextField as MaterialTextField, TextFieldProps as MaterialTextFieldProps } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import React, { ChangeEventHandler, FocusEventHandler, useMemo } from 'react';

import { FormHelperText } from '../FormHelperText/FormHelperText';
import { TooltipButton } from '../TooltipButton/TooltipButton';

/**
 * TestFieldProps
 *
 * Note that repeating the EventHandler here is merely for Storybook,
 * so they are wrapped by the action plugin
 */

export interface TextFieldProps extends Omit<MaterialTextFieldProps, 'margin'> {
  /**
   * Set a tooltip and show a TooltipButton as endAdornment
   */
  tooltip?: string;
  /**
   * Focus Event: onBlur
   * @see [https://reactjs.org/docs/events.html#onblur]
   */
  onBlur?: FocusEventHandler<HTMLInputElement>;
  /**
   * Focus Event: onFocus
   * @see [https://reactjs.org/docs/events.html#onfocus]
   */
  onFocus?: FocusEventHandler<HTMLInputElement>;
  /**
   * Change Event: onChange
   * @see [https://reactjs.org/docs/events.html#onchange]
   */
  onChange?: ChangeEventHandler<HTMLInputElement>;
}

/**
 * The TextField
 *
 * @version [AIV]{version}[/AIV]
 * @author [SFP](https://gitlab.sfp.berlin/products/sparkasse.de/portals)
 * @see [https://styleguide.sparkasse.de/document/69#/form-komponenten/textfield]
 */
export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  ({ variant = 'outlined', tooltip, slotProps, error, ...props }, ref) => {
    const InputProps = useMemo(() => {
      const endAdornment = tooltip ? (
        <InputAdornment position="start">
          <TooltipButton title={tooltip} />
        </InputAdornment>
      ) : undefined;

      if (variant === 'filled') {
        return { disableUnderline: true, endAdornment, ...slotProps?.input };
      }
      return { endAdornment, ...slotProps?.input };
    }, [tooltip, variant, slotProps?.input]);

    const FormHelperTextProps = useMemo(() => {
      return {
        component: (props) => <FormHelperText error={error} {...props} />,
        ...slotProps?.formHelperText,
      };
    }, [slotProps?.formHelperText, error]);

    return (
      <MaterialTextField
        ref={ref}
        variant={variant}
        {...props}
        error={error}
        slotProps={{ ...slotProps, input: InputProps, formHelperText: FormHelperTextProps }}
        data-test={props?.name}
      />
    );
  }
);

TextField.displayName = 'TextField';
